import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GenericService } from '../generic.service';
import {
  StudentEducationRequest,
  StudentEducationResponse,
  StudentEducationListResponse
} from '../../models/onboarding/student-education';
import {
  TestScoreRequest,
  TestScoreResponse
} from '../../models/onboarding/test-scores';
import {
  StudentAwardRequest,
  StudentAwardResponse
} from '../../models/onboarding/student-awards';
import {
  RecommendationRequest,
  RecommendationResponse
} from '../../models/onboarding/recommendations';
import {
  ExperienceRequest,
  ExperienceResponse
} from '../../models/onboarding/student-experience';
import { environment } from '../../../environments/environment';

export interface Board {
  id: number;
  name: string;
}

interface BoardsResponse {
  total: number;
  offset: number;
  result: Board[];
}

export interface DocumentParserRequest {
  file: File;
  doc_type: string; 
}

export interface DocumentParserResponse {
  result: {
    extracted_data: {
      name?: string;
      dob?: string;
      subject_and_grades?: Array<{
        SubjectName: string;
        Grade: string;
      }>;
      total_grade?: string;
      exam_type?: string;
      score?: string;
      [key: string]: any;
    };
  };
  status?: string;
}

export interface DocumentParseJobResponse {
  job_id: string;
  status: string; 
  result?: DocumentParserResponse['result'];
}

@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private readonly documentParserBaseUrl = environment.documentParserBaseUrl;

  constructor(private genericService: GenericService) { }

  getBoards(): Observable<Board[]> {
    return this.genericService.get<BoardsResponse>('boards').pipe(
      map(response => response.result)
    );
  }

    // --- STEP 1: Education ---
  parseDocument(file: File, docType: string): Observable<DocumentParseJobResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    const url = `${this.documentParserBaseUrl}/parse`;
    return this.genericService.postThirdPartyFormData<DocumentParseJobResponse>(url, formData);
  }

  getDocumentParseResult(jobId: string): Observable<DocumentParseJobResponse> {
    const url = `${this.documentParserBaseUrl}/jobs/${jobId}`;
    return this.genericService.getThirdPartyResponse<DocumentParseJobResponse>(url);
  }
  getEducations(studentId?: string): Observable<StudentEducationListResponse> {
    return this.genericService.get<StudentEducationListResponse>(`student/education${studentId ? `?studentId=${studentId}` : ''}`);
  }

  createEducation(formData: FormData): Observable<StudentEducationResponse> {
    return this.genericService.postFormData<StudentEducationResponse>('student/education', formData);
  }

  updateEducation(id: number, formData: FormData): Observable<StudentEducationResponse> {
    return this.genericService.putFormData<StudentEducationResponse>(`student/education/${id}`, formData);
  }

  deleteEducation(id: number): Observable<void> {
    return this.genericService.delete<void>(`student/education/${id}`);
  }

    // --- STEP 2: Test Scores ---
  getTestScores(studentId: string): Observable<any> {
    return this.genericService.get<any>(`testscore/${studentId}`);
  }

  createTestScore(payload: any): Observable<any> {
    return this.genericService.post<any>('testscore', payload);
  }

  updateTestScore(payload: any): Observable<any> {
    return this.genericService.put<any>('testscore', payload);
  }

  // --- STEP 3: Recommendations ---
  getRecommendations(studentId?: string): Observable<RecommendationResponse[]> {
    return this.genericService.get<RecommendationResponse[]>(`recommendation${studentId ? `?studentId=${studentId}` : ''}`);
  }

  createRecommendation(formData: FormData): Observable<RecommendationResponse> {
    return this.genericService.postFormData<RecommendationResponse>('recommendation', formData);
  }

  updateRecommendation(studentrecommendationid: number, formData: FormData): Observable<RecommendationResponse> {
    return this.genericService.putFormData<RecommendationResponse>(`recommendation/${studentrecommendationid}`, formData);
  }

  deleteRecommendation(recommendationId: number): Observable<void> {
    return this.genericService.delete<void>(`recommendation/${recommendationId}`);
  }

  // --- STEP 4: Experiences ---
  getExperiences(studentId?: string): Observable<ExperienceResponse[]> {
    return this.genericService.get<ExperienceResponse[]>(`student/experience${studentId ? `?studentId=${studentId}` : ''}`);
  }

  createExperience(formData: FormData): Observable<ExperienceResponse> {
    return this.genericService.postFormData<ExperienceResponse>('student/experience', formData);
  }

  updateExperience(id: number, formData: FormData): Observable<ExperienceResponse> {
    return this.genericService.putFormData<ExperienceResponse>(`student/experience/${id}`, formData);
  }

  deleteExperience(experienceId: number): Observable<void> {
    return this.genericService.delete<void>(`student/experience/${experienceId}`);
  }

  // --- STEP 5: Awards & Accolades ---
  getAwards(studentId?: string): Observable<StudentAwardResponse[]> {
    return this.genericService.get<StudentAwardResponse[]>(`student/awards-and-accolade${studentId ? `?studentId=${studentId}` : ''}`);
  }

  createAward(formData: FormData): Observable<StudentAwardResponse> {
    return this.genericService.postFormData<StudentAwardResponse>('student/awards-and-accolade', formData);
  }

  updateAward(id: number, formData: FormData): Observable<StudentAwardResponse> {
    return this.genericService.putFormData<StudentAwardResponse>(`student/awards-and-accolade/${id}`, formData);
  }

  deleteAward(awarddsandAccoladeId: number): Observable<void> {
    return this.genericService.delete<void>(`student/awards-and-accolade/${awarddsandAccoladeId}`);
  }

}
